import { useEffect, useState } from 'react';
import { ClockCounterClockwise, Eye, GitDiff, CheckCircle, ArrowUUpLeft, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { engApi } from '../engApi';
import { VL, versionErrorText } from './digestVersionI18n';
import { LifecycleBadge, VersionMeta } from './DigestVersionBadge';
import { jstDateTime } from '@/utils/jstDate';

// バージョン履歴ダイアログ — 一覧・表示・比較（2件選択）・有効化・復元 + 有効化の履歴
export function DigestVersionsPanel({ sid, open, onOpenChange, lang, can, refreshKey, onView, onActivate, onRestore, onCompare }) {
  const L = VL[lang] || VL.ja;
  const [data, setData] = useState(null);
  const [pick, setPick] = useState([]);

  useEffect(() => {
    if (!open || !sid) return;
    setData(null); setPick([]);
    engApi.digestVersions(sid).then(setData)
      .catch((e) => { setData({ versions: [], activations: [] }); toast.error(versionErrorText(e, lang, L.history)); });
  }, [open, sid, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePick = (id) => setPick((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p.slice(-1), id]));
  const versions = data?.versions || [];
  const activeId = data?.series?.active_version_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="digest-versions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockCounterClockwise size={16} weight="duotone" className="text-violet-600" /> {L.history}
          </DialogTitle>
          <p className="text-[11px] text-zinc-500">{L.historyHint}</p>
        </DialogHeader>
        {data === null ? (
          <div className="py-10 text-center text-sm text-zinc-400">…</div>
        ) : versions.length === 0 ? (
          <div className="py-10 text-center text-sm text-zinc-400" data-testid="digest-versions-empty">{L.noVersions}</div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 text-[11px] text-zinc-500">
              <span>{can.compare ? L.selectTwo : ''}</span>
              {can.compare && (
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={pick.length !== 2}
                  onClick={() => onCompare(pick[0], pick[1])} data-testid="digest-versions-compare-btn">
                  <GitDiff size={12} className="mr-1" /> {L.compare}
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.id} className={`rounded-lg border px-3 py-2 ${v.lifecycle === 'active' ? 'border-emerald-200 bg-emerald-50/40' : 'border-zinc-200'}`}
                  data-testid={`digest-version-row-${v.id}`}>
                  <div className="flex items-start gap-2">
                    {can.compare && (
                      <input type="checkbox" className="mt-1" checked={pick.includes(v.id)} onChange={() => togglePick(v.id)}
                        disabled={!v.sealed || !['complete', 'complete_with_warnings'].includes(v.status)}
                        data-testid={`digest-version-pick-${v.id}`} aria-label={`select v${v.version_number}`} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[13px] font-bold text-zinc-800">{L.version(v.version_number)}</span>
                        <LifecycleBadge v={v} lang={lang} />
                        {v.board_ready === true && <span className="text-[10px] text-emerald-700">{L.boardReady}</span>}
                      </div>
                      <VersionMeta v={v} lang={lang} />
                      {v.error && <p className="text-[11px] text-rose-600 mt-0.5 whitespace-pre-wrap">{v.error}</p>}
                      {v.operation_id && (
                        <p className="text-[10px] text-zinc-400 mt-0.5 font-mono" data-testid={`digest-version-op-${v.id}`}>
                          {L.aiOpsId}: {v.operation_id}
                          {can.aiLogs && (
                            <a href={`/settings/ai-operations/${v.operation_id}`} target="_blank" rel="noreferrer"
                              className="ml-1 inline-flex items-center text-indigo-600 hover:underline" data-testid={`digest-version-oplink-${v.id}`}>
                              {L.aiOps} <ArrowSquareOut size={10} className="ml-0.5" />
                            </a>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {v.sealed && ['complete', 'complete_with_warnings'].includes(v.status) && (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onView(v)} data-testid={`digest-version-view-${v.id}`}>
                          <Eye size={12} className="mr-1" /> {L.view}
                        </Button>
                      )}
                      {can.activate && v.activatable && (
                        <Button size="sm" className="h-7 text-[11px] bg-emerald-700 hover:bg-emerald-800" onClick={() => onActivate(v)}
                          data-testid={`digest-version-activate-${v.id}`}>
                          <CheckCircle size={12} className="mr-1" /> {L.activate}
                        </Button>
                      )}
                      {can.restore && v.sealed && ['complete', 'complete_with_warnings'].includes(v.status) && v.id !== activeId && v.integrity_ok !== false && (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onRestore(v)} data-testid={`digest-version-restore-${v.id}`}>
                          <ArrowUUpLeft size={12} className="mr-1" /> {L.restore}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {(data.activations || []).length > 0 && (
              <div className="pt-3 border-t border-zinc-200" data-testid="digest-activations">
                <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">{L.activations}</p>
                <ul className="space-y-1">
                  {data.activations.map((a) => (
                    <li key={a.id} className="text-[11px] text-zinc-600" data-testid={`digest-activation-${a.id}`}>
                      <span className="font-mono text-zinc-400">{jstDateTime(a.at)}</span>{' '}
                      v{a.previous_version_number ?? '—'} → <b>v{a.version_number}</b>{' · '}{L.activatedBy(a.actor, a.kind)}
                      {a.acknowledged_warnings && <span className="ml-1 text-amber-700">⚠</span>}
                      {a.reason && <span className="ml-1 text-zinc-400">「{a.reason}」</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
